import { useEffect } from 'react';
import { TrendingUp, Users, FileText, Database, Activity, CheckCircle } from 'lucide-react';

const LAND_ACQUISITION_PORTAL_ID = 4;
const LAND_ACQUISITION_SSO = {
  superadmin: 'https://laop-adtw.in/admin-sso/callback',
  districtuser: 'https://laop-adtw.in/sso/callback',
};

const Dashboard = () => {
  useEffect(() => {
    const portalsStr = localStorage.getItem('portals');
    const portals = portalsStr ? JSON.parse(portalsStr) : [];
    const hasLandAcquisition = portals.some(p => p.portal_id === LAND_ACQUISITION_PORTAL_ID);

    if (hasLandAcquisition) {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const parsedUser = userStr ? JSON.parse(userStr) : {};
      const email = parsedUser.email || '';
      const userRole = localStorage.getItem('userRole') || 'superadmin';
      const callbackUrl = LAND_ACQUISITION_SSO[userRole] || LAND_ACQUISITION_SSO['superadmin'];
      window.location.href = `${callbackUrl}?token=${token}&username=${email}`;
    }
  }, []);
  // Test metrics data
  const metrics = [
    { 
      label: 'Test Metric A', 
      value: '1,234', 
      change: '+12.5%', 
      icon: TrendingUp,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    { 
      label: 'Test Metric B', 
      value: '567', 
      change: '+8.3%', 
      icon: Users,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    { 
      label: 'Test Metric C', 
      value: '89', 
      change: '+15.7%', 
      icon: FileText,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    { 
      label: 'Test Metric D', 
      value: '345', 
      change: '+5.2%', 
      icon: Database,
      bgColor: 'bg-yellow-50',
      iconColor: 'text-yellow-600'
    },
    { 
      label: 'Test Metric E', 
      value: '678', 
      change: '+20.1%', 
      icon: Activity,
      bgColor: 'bg-pink-50',
      iconColor: 'text-pink-600'
    },
    { 
      label: 'Test Metric F', 
      value: '912', 
      change: '+9.8%', 
      icon: CheckCircle,
      bgColor: 'bg-indigo-50',
      iconColor: 'text-indigo-600'
    }
  ];

  // Test table data
  const recentItems = [
    { id: 'TEST001', name: 'Test Item Alpha', status: 'Active', date: '2024-01-15', category: 'Type A' },
    { id: 'TEST002', name: 'Test Item Beta', status: 'Pending', date: '2024-01-14', category: 'Type B' },
    { id: 'TEST003', name: 'Test Item Gamma', status: 'Active', date: '2024-01-13', category: 'Type A' },
    { id: 'TEST004', name: 'Test Item Delta', status: 'Completed', date: '2024-01-12', category: 'Type C' },
    { id: 'TEST005', name: 'Test Item Epsilon', status: 'Active', date: '2024-01-11', category: 'Type B' },
    { id: 'TEST006', name: 'Test Item Zeta', status: 'Pending', date: '2024-01-10', category: 'Type A' },
    { id: 'TEST007', name: 'Test Item Eta', status: 'Completed', date: '2024-01-09', category: 'Type C' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-700';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'Completed':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Metrics Cards - 3 columns per row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div 
              key={index} 
              className={`${metric.bgColor} p-6 border border-gray-200`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${metric.bgColor} flex items-center justify-center border border-gray-200`}>
                  <Icon className={`w-6 h-6 ${metric.iconColor}`} />
                </div>
                <span className="text-sm font-semibold text-green-600">{metric.change}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-1">{metric.value}</h3>
              <p className="text-sm text-gray-600">{metric.label}</p>
            </div>
          );
        })}
      </div>

      {/* Recent Items Table */}
      <div className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Recent Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Test ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Test Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Test Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Test Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Test Category
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-semibold ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.category}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;